import React from "react";
import { useParams, Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { PageTopNav } from "@/components/nav/PageTopNav";

interface Species {
  id: string;
  typeIndex: number;
  commonType: string;
  englishName: string;
  btoEnglishName: string;
  order: string;
  family: string;
  genus: string;
  scientificName: string;
  authority: string;
  breedingRegions: string;
  breedingSubRegions: string;
  nonBreedingRegions: string;
  createdAt: string;
  updatedAt: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export function AdminSpeciesDetail() {
  const { speciesId } = useParams();

  const {
    data: species,
    isLoading,
    error,
  } = useQuery<Species>({
    queryKey: ["species", speciesId],
    queryFn: async () => {
      const res = await fetch(`/api/species/${speciesId}`);
      if (!res.ok) throw new Error("Failed to fetch species");
      return res.json();
    },
    enabled: !!speciesId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !species) {
    return (
      <div className="p-8 text-center text-red-600">Failed to load species details.</div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 md:px-0 py-8">
      <PageTopNav pageName={species.englishName} />
      <div>
        <div>
          <p>{species.scientificName} {species.authority && <span className="text-muted-foreground">({species.authority})</span>}</p>
        </div>
        <div>
          <div className="space-y-4">
            {species.btoEnglishName && species.btoEnglishName !== species.englishName && (
              <div>
                <span className="text-sm font-medium">BTO Name:</span> {species.btoEnglishName}
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm font-medium">Order:</span> {species.order}
              </div>
              <div>
                <span className="text-sm font-medium">Family:</span> {species.family}
              </div>
              <div>
                <span className="text-sm font-medium">Genus:</span> {species.genus}
              </div>
            </div>
            {species.commonType && (
              <div>
                <span className="text-sm font-medium">Common Type:</span> {species.commonType}
              </div>
            )}
            {species.breedingRegions && (
              <div>
                <span className="text-sm font-medium">Breeding Regions:</span>
                <p className="mt-1 text-sm p-2 bg-muted rounded-md">{species.breedingRegions}</p>
              </div>
            )}
            {species.breedingSubRegions && (
              <div>
                <span className="text-sm font-medium">Breeding Subregions:</span>
                <p className="mt-1 text-sm p-2 bg-muted rounded-md">{species.breedingSubRegions}</p>
              </div>
            )}
            {species.nonBreedingRegions && (
              <div>
                <span className="text-sm font-medium">Non-breeding Regions:</span>
                <p className="mt-1 text-sm p-2 bg-muted rounded-md">{species.nonBreedingRegions}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <span className="text-sm font-medium">Created:</span> {formatDate(species.createdAt)}
              </div>
              <div>
                <span className="text-sm font-medium">Last Updated:</span> {formatDate(species.updatedAt)}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

